import React,{useState,useEffect} from 'react';
import {useNavigate} from 'react-router-dom';
import {HashLink as Link} from 'react-router-hash-link';
import {ChevronDownIcon,UserIcon,LogoutIcon,CreditCardIcon,MenuIcon,XIcon} from '@heroicons/react/outline';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faFeather,faCrown} from '@fortawesome/free-solid-svg-icons';
import {faHeart,faGem} from '@fortawesome/free-regular-svg-icons';
import {enqueueSnackbar} from 'notistack';
import {sprintf} from 'sprintf-js';
import getSortedLangsUtil from '../../utils/get-sorted-langs-util';
import globalSettingsManagerUtil from '../../utils/global-settings-manager-util';
import sendReqUtil from '../../utils/send-req-util';
import {useQuota} from '../../utils/use-quota-util';
import logoFullDark from '../../assets/images/logo-full-dark.png';
import locales from '../../locales/locales';
import langMap from '../../constants/lang-map';
import plans from '../../constants/plans';
const Navbar=({disableFixed,globalSettings})=>{
  const [isLangDropdownOpen,setIsLangDropdownOpen]=useState(false);
  const [isUserDropdownOpen,setIsUserDropdownOpen]=useState(false);
  const [isMobileMenuOpen,setIsMobileMenuOpen]=useState(false);
  const [isMobileLangDropdownOpen,setIsMobileLangDropdownOpen]=useState(false);
  const navigate=useNavigate();
  const quota=useQuota([globalSettings.token],(isUserDropdownOpen&&!isMobileMenuOpen)||isMobileMenuOpen);
  const iconMap={faHeart,faFeather,faGem,faCrown};
  useEffect(()=>{
    const handleClickOutside=(e)=>{
      if(isLangDropdownOpen&&!e.target.closest('.lang-dropdown-container')){
        setIsLangDropdownOpen(false);
      }
      if(isUserDropdownOpen&&!e.target.closest('.user-dropdown-container')){
        setIsUserDropdownOpen(false);
      }
      if(isMobileMenuOpen&&!e.target.closest('.mobile-menu-container')){
        setIsMobileMenuOpen(false);
      }
    };
    document.addEventListener('mousedown',handleClickOutside);
    return ()=>{
      document.removeEventListener('mousedown',handleClickOutside);
    };
  },[isLangDropdownOpen,isUserDropdownOpen,isMobileMenuOpen]);
  const handleLogout=()=>{
    setIsUserDropdownOpen(false);
    setIsMobileMenuOpen(false);
    globalSettingsManagerUtil.updateGlobalSettings({
      token:null,
    });
    enqueueSnackbar(
      locales.navLogoutMessage[globalSettings.lang]
    ,{variant:'success'});
  };
  const getLangSwitchPath=(targetLang)=>{
    const pathParts=window.location.pathname.split('/').filter(Boolean);
    if(pathParts.length===0){
      return `/${targetLang}${window.location.hash}`;
    }
    pathParts[0]=targetLang;
    return `/${pathParts.join('/')}${window.location.hash}`;
  };
  const handleUpgrade=async ()=>{
    setIsUserDropdownOpen(false);
    setIsMobileMenuOpen(false);
    if(globalSettings.user?.cust_id){
      const res=await sendReqUtil('/get-paddle-portal-link');
      console.assert(res.body&&res.status===200);
      window.open(res.body.paddle_portal_link,'_blank');
    }
    else if(window.location.pathname.startsWith('/pricing')){
      document.scrollingElement.scrollTo(0,0);
    }
    else{
      navigate(`/${globalSettings.lang}/pricing`);
    }
  };
  const email=globalSettings.user?globalSettings.user.email:null;
  return (
    <nav className={`bg-white border-b border-gray-200 ${disableFixed?'absolute':'fixed'} w-full z-20`}>
      <div className='w-full px-4 sm:px-6 lg:px-8'>
        <div className='flex justify-between h-16'>
          <div className='flex'>
            <div className='flex-shrink-0 flex items-center'>
              <Link to={`/${globalSettings.lang}/`}>
                <img className='h-[32px]' src={logoFullDark}/>
              </Link>
            </div>
            <div className='hidden 2xl:ml-6 2xl:flex 2xl:space-x-4'>
              <Link
                to={`/${globalSettings.lang}#browser-extension`}
                className='border-transparent text-gray-700 hover:text-purple-600 inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium'
              >
                {locales.navBrowserExtension[globalSettings.lang]}
              </Link>
              <Link to={`/${globalSettings.lang}#features`} className='border-transparent text-gray-700 hover:text-purple-600 inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium'>
                {locales.navFeatures[globalSettings.lang]}
              </Link>
              <Link to={`/${globalSettings.lang}#faq`} className='border-transparent text-gray-700 hover:text-purple-600 inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium'>
                {locales.navFAQ[globalSettings.lang]}
              </Link>
              <Link to={`/${globalSettings.lang}/pricing`} className='border-transparent text-gray-700 hover:text-purple-600 inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium'>
                {locales.navPricing[globalSettings.lang]}
              </Link>
              <Link to={`/${globalSettings.lang}/translator`} className='border-transparent text-gray-700 hover:text-purple-600 inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium'>
                {locales.navTranslator[globalSettings.lang]}
              </Link>
              <Link to={`/${globalSettings.lang}/contact`} className='border-transparent text-gray-700 hover:text-purple-600 inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium'>
                {locales.navFeedback[globalSettings.lang]}
              </Link>
            </div>
          </div>
          <div className='flex items-center 2xl:hidden mobile-menu-container'>
            <button
              onClick={()=>setIsMobileMenuOpen(!isMobileMenuOpen)}
              className='inline-flex items-center justify-center p-2 rounded-md text-gray-700 hover:text-purple-600 hover:bg-gray-100'
            >
              {isMobileMenuOpen?<XIcon className='h-6 w-6'/>:<MenuIcon className='h-6 w-6'/>}
            </button>
          </div>
          <div className='hidden 2xl:ml-6 2xl:flex 2xl:items-center'>
            <div className='relative lang-dropdown-container'>
              <button 
                className='bg-white px-4 py-2 text-sm text-gray-700 hover:text-purple-600 flex items-center space-x-2 rounded-md border border-gray-300'
                onClick={() => setIsLangDropdownOpen(!isLangDropdownOpen)}
              >
                <span
                  dangerouslySetInnerHTML={{__html:`${langMap[globalSettings.lang].flag}&ensp;${langMap[globalSettings.lang].name[globalSettings.lang]}`}}
                  className='mr-2'
                ></span>
                <ChevronDownIcon className='h-4 w-4 text-gray-500' />
              </button>
              <div className={`absolute left-0 mt-2 w-48 max-h-80 overflow-y-auto bg-white rounded-md shadow-lg py-1 ring-1 ring-black ring-opacity-5 z-30 ${!isLangDropdownOpen?'hidden':''}`}>{
                getSortedLangsUtil('en').map((x)=>(
                  <Link
                    key={x}
                    to={getLangSwitchPath(x)}
                    onClick={()=>setIsLangDropdownOpen(false)}
                    className='block px-4 py-2 text-sm text-gray-700 hover:bg-purple-50 flex items-center cursor-pointer'
                  >
                    <span
                      dangerouslySetInnerHTML={{__html:`${langMap[x].flag}&ensp;${langMap[x].name[x]}`}}
                      className='mr-2'
                    ></span>
                  </Link>
                ))
              }</div>
            </div>
            {email&&(
              <div className='ml-4'>
                <div className='bg-white px-3 py-2 text-sm border border-gray-200 rounded-md flex items-center shadow-sm hover:shadow-md transition-shadow'>
                  <FontAwesomeIcon 
                    icon={iconMap[plans[globalSettings.user.level].icon]} 
                    className='h-4 w-4 text-purple-600 mr-2' 
                  />
                  <span className='font-medium text-gray-700 capitalize'>
                    {plans[globalSettings.user.level].name}
                  </span>
                </div>
              </div>
            )}
            {email?(
              <div className='relative ml-4 user-dropdown-container'>
                <button
                  onClick={()=>{setIsUserDropdownOpen(!isUserDropdownOpen);}}
                  className='bg-purple-100 px-4 py-2 text-sm text-gray-700 hover:bg-purple-200 flex items-center space-x-2 rounded-md'
                >
                  <UserIcon className='h-5 w-5 text-purple-700 mr-2' />
                  <span className='max-w-[150px] truncate'>{email}</span>
                  <ChevronDownIcon className='h-4 w-4 text-gray-500' />
                </button>
                <div className={`absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 ring-1 ring-black ring-opacity-5 z-30 ${!isUserDropdownOpen?'hidden':''}`}>
                  <div className='px-4 py-2 text-sm text-gray-500 border-b border-gray-200'>
                    {sprintf(locales.navBalance[globalSettings.lang],(quota<0)?'?':quota)}
                  </div>
                  <button
                    onClick={handleUpgrade}
                    className='block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-purple-50 flex items-center'
                  >
                    <CreditCardIcon className='h-5 w-5 text-purple-600 mr-2' />
                    {
                      globalSettings.user.cust_id?
                      locales.navBilling[globalSettings.lang]:
                      locales.navUpgrade[globalSettings.lang]
                    }
                  </button>
                  <button
                    className='block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-purple-50 flex items-center'
                    onClick={handleLogout}
                  >
                    <LogoutIcon className='h-5 w-5 text-purple-600 mr-2' />
                    {locales.navLogout[globalSettings.lang]}
                  </button>
                </div>
              </div>
            ):(
              <>
                <Link to={`/${globalSettings.lang}/login`} className='inline-block ml-4'>
                  <button className='bg-purple-800 hover:bg-purple-600 text-white px-4 py-2 rounded-md text-sm font-medium'>
                    {locales.navSignIn[globalSettings.lang]}
                  </button>
                </Link>
                <Link to={`/${globalSettings.lang}/register`} className='inline-block ml-4'>
                  <button className='bg-purple-600 hover:bg-purple-800 text-white px-4 py-2 rounded-md text-sm font-medium'>
                    {locales.navCreateAccount[globalSettings.lang]}
                  </button>
                </Link>
              </>
            )}
          </div>
        </div>
      </div>
      <div className={`2xl:hidden mobile-menu-container bg-white border-b border-gray-200 overflow-y-auto max-h-[calc(100vh-4rem)] ${!isMobileMenuOpen?'hidden':''}`}>
        <div className='px-4 pt-2 pb-3 space-y-1'>
            <Link
              to={`/${globalSettings.lang}#browser-extension`}
              onClick={()=>{setIsMobileMenuOpen(false);}}
              className='block px-3 py-2 text-base font-medium text-gray-700 hover:text-purple-600 hover:bg-gray-50'
            >
              {locales.navBrowserExtension[globalSettings.lang]}
            </Link>
            <Link to={`/${globalSettings.lang}#features`} onClick={()=>setIsMobileMenuOpen(false)} className='block px-3 py-2 text-base font-medium text-gray-700 hover:text-purple-600 hover:bg-gray-50'>
              {locales.navFeatures[globalSettings.lang]}
            </Link>
            <Link to={`/${globalSettings.lang}#faq`} onClick={()=>setIsMobileMenuOpen(false)} className='block px-3 py-2 text-base font-medium text-gray-700 hover:text-purple-600 hover:bg-gray-50'>
              {locales.navFAQ[globalSettings.lang]}
            </Link>
            <Link to={`/${globalSettings.lang}/pricing`} onClick={()=>setIsMobileMenuOpen(false)} className='block px-3 py-2 text-base font-medium text-gray-700 hover:text-purple-600 hover:bg-gray-50'>
              {locales.navPricing[globalSettings.lang]}
            </Link>
            <Link to={`/${globalSettings.lang}/translator`} onClick={()=>setIsMobileMenuOpen(false)} className='block px-3 py-2 text-base font-medium text-gray-700 hover:text-purple-600 hover:bg-gray-50'>
              {locales.navTranslator[globalSettings.lang]}
            </Link>
            <Link to={`/${globalSettings.lang}/contact`} onClick={()=>setIsMobileMenuOpen(false)} className='block px-3 py-2 text-base font-medium text-gray-700 hover:text-purple-600 hover:bg-gray-50'>
              {locales.navFeedback[globalSettings.lang]}
            </Link>
          </div>
          <div className='pt-4 pb-3 border-t border-gray-200'>
            <div className='px-4 space-y-3'>
              <button
                onClick={() => setIsMobileLangDropdownOpen(!isMobileLangDropdownOpen)}
                className='w-full flex items-center justify-between px-3 py-2 text-sm font-medium text-gray-500 uppercase tracking-wide hover:text-purple-600 rounded-md hover:bg-gray-50'
              >
                <span>
                  {locales.navLanguage[globalSettings.lang]}
                </span>
                <ChevronDownIcon className={`h-4 w-4 transition-transform ${isMobileLangDropdownOpen ? 'transform rotate-180' : ''}`} />
              </button>
              <div className={`space-y-2 ${!isMobileLangDropdownOpen?'hidden':''}`}>{
                getSortedLangsUtil('en').map((x)=>(
                  <Link
                    key={x}
                    to={getLangSwitchPath(x)}
                    onClick={() => setIsMobileLangDropdownOpen(false)}
                    className={`block w-full text-left px-3 py-2 text-base font-medium ${globalSettings.lang===x?'bg-purple-50 text-purple-700':'text-gray-700 hover:text-purple-600 hover:bg-gray-50'} rounded-md flex items-center`}
                  >
                    <span
                      dangerouslySetInnerHTML={{__html:`${langMap[x].flag}&ensp;${langMap[x].name[x]}`}}
                    ></span>
                  </Link>
                ))
              }</div>
            </div>
          </div>
          <div className='pt-4 pb-3 border-t border-gray-200'>
            <div className='px-4 space-y-1'>
              {email?(
                <>
                  <div className='space-y-3'>
                    <div className='space-y-2'>
                      <div className='px-3 py-2'>
                        <div className='flex items-center space-x-3'>
                          <div className='max-w-[300px] group'>
                            <span 
                              className='text-base font-medium text-gray-900 truncate block group-hover:whitespace-normal group-hover:overflow-visible transition-all cursor-pointer'
                              title={email}
                            >
                              {email}
                            </span>
                          </div>
                          {globalSettings.user&&(
                            <div className='inline-flex items-center px-2 py-1 text-xs border border-gray-200 rounded-md bg-gray-50'>
                              <FontAwesomeIcon 
                                icon={iconMap[plans[globalSettings.user.level].icon]} 
                                className='h-3 w-3 text-purple-600 mr-1' 
                              />
                              <span className='font-medium text-gray-700 capitalize'>
                                {plans[globalSettings.user.level].name}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                      {globalSettings.user&&(
                        <div className='px-3 py-2 text-base font-medium text-gray-600'>
                          {sprintf(locales.navBalance[globalSettings.lang],(quota<0)?'?':quota)}
                        </div>
                      )}
                    </div>
                    <div className='space-y-1'>
                      <button onClick={handleUpgrade} className='block w-full text-left px-3 py-2 text-base font-medium text-gray-700 hover:text-purple-600 hover:bg-gray-50 rounded-md'>
                        {
                          globalSettings.user.cust_id?
                          locales.navBilling[globalSettings.lang]:
                          locales.navUpgrade[globalSettings.lang]
                        }
                      </button>
                      <button onClick={()=>{handleLogout();setIsMobileMenuOpen(false);}} className='block w-full text-left px-3 py-2 text-base font-medium text-gray-700 hover:text-purple-600 hover:bg-gray-50 rounded-md'>
                        {locales.navLogout[globalSettings.lang]}
                      </button>
                    </div>
                  </div>
                </>
              ):(
                <>
                  <Link to={`/${globalSettings.lang}/login`} onClick={()=>setIsMobileMenuOpen(false)} className='block px-3 py-2 text-base font-medium bg-purple-800 hover:bg-purple-600 text-white rounded-md text-center'>
                    {locales.navSignIn[globalSettings.lang]}
                  </Link>
                  <Link to={`/${globalSettings.lang}/register`} onClick={()=>setIsMobileMenuOpen(false)} className='block px-3 py-2 text-base font-medium bg-purple-600 hover:bg-purple-800 text-white rounded-md text-center'>
                    {locales.navCreateAccount[globalSettings.lang]}
                  </Link>
                </>
              )
            }
          </div>
        </div>
      </div>
    </nav>
  );
};
export default Navbar;

